import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { formatCurrency, formatNumber } from '../../lib/utils';
import { Card, CardHeader, CardContent } from '../../components/ui/Card';
import {
  Users, TrendingUp, ArrowDownCircle, ArrowUpCircle, Clock, Shield, Wallet, UserPlus,
  AlertTriangle, DollarSign, Activity, Server, Database, RefreshCw, MessageSquare, ChevronRight, BarChart3
} from 'lucide-react';

const dateRanges = [
  { label: 'Today', value: 'today' }, { label: '7 Days', value: '7d' },
  { label: '30 Days', value: '30d' }, { label: 'Month', value: 'month' },
];

export default function AdminDashboardPage() {
  const [dateRange, setDateRange] = useState('30d');
  const { data: stats } = useQuery({
    queryKey: ['adminDashboard', dateRange],
    queryFn: () => api.get('/admin/dashboard-stats', { params: { range: dateRange } }).then(r => r.data),
  });

  const kpiCards = [
    { title: 'Total Users', value: formatNumber(stats?.totalUsers || 0), icon: Users, link: '/admin/users' },
    { title: 'Active Users', value: formatNumber(stats?.activeUsers || 0), icon: UserPlus, link: '/admin/users' },
    { title: 'Total Deposits', value: formatCurrency(stats?.totalDeposits || 0), icon: ArrowDownCircle, link: '/admin/deposits' },
    { title: 'Total Investments', value: formatCurrency(stats?.totalInvestments || 0), icon: TrendingUp, link: '/admin/investments' },
    { title: 'Total Withdrawals', value: formatCurrency(stats?.totalWithdrawals || 0), icon: ArrowUpCircle, link: '/admin/withdrawals' },
    { title: 'Total Earnings', value: formatCurrency(stats?.totalEarnings || 0), icon: DollarSign, link: '/admin/reports' },
  ];

  const pendingActions = [
    { label: 'Pending KYC', count: stats?.pendingKyc || 0, link: '/admin/kyc', icon: Shield },
    { label: 'Pending Withdrawals', count: stats?.pendingWithdrawals || 0, link: '/admin/withdrawals', icon: ArrowUpCircle },
    { label: 'Pending Deposits', count: stats?.pendingDeposits || 0, link: '/admin/deposits', icon: ArrowDownCircle },
    { label: 'Support Tickets', count: stats?.openTickets || 0, link: '/admin/support', icon: MessageSquare },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="d-page-header">
          <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
          <p className="text-slate-400 mt-1">Platform operations center</p>
        </div>
        <div className="flex items-center gap-1.5 p-1 bg-white/[0.04] rounded-xl">
          {dateRanges.map(dr => (
            <button key={dr.value} onClick={() => setDateRange(dr.value)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${dateRange === dr.value ? 'bg-primary-600 text-white shadow-glow' : 'text-slate-400 hover:text-white hover:bg-white/[0.04]'}`}>
              {dr.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpiCards.map((kpi, i) => {
          const Icon = kpi.icon;
          return (
            <Link key={i} to={kpi.link} className="d-card-hover p-4 group">
              <div className="flex items-center justify-between mb-3">
                <div className="w-9 h-9 rounded-xl bg-white/[0.04] border border-white/[0.06] flex items-center justify-center">
                  <Icon className="w-4 h-4 text-slate-400" />
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-slate-600 group-hover:text-primary-400 transition-colors" />
              </div>
              <p className="text-xs text-slate-500 truncate">{kpi.title}</p>
              <p className="text-lg font-bold text-white mt-0.5">{kpi.value}</p>
            </Link>
          );
        })}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Chart placeholder */}
          <Card>
            <CardHeader><h2 className="text-sm font-semibold text-white">Financial Trend</h2></CardHeader>
            <CardContent>
              <div className="h-64 flex items-center justify-center rounded-xl bg-white/[0.02] border border-white/[0.04]">
                <div className="text-center">
                  <BarChart3 className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">Chart data loads from API</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card>
            <CardHeader><h2 className="text-sm font-semibold text-white">Recent Activity</h2></CardHeader>
            <CardContent>
              <div className="space-y-0">
                {(stats?.recentActivity || []).slice(0, 8).map((activity: any, i: number) => (
                  <div key={i} className="flex items-center gap-3 py-3 border-b border-white/[0.04] last:border-0">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                      activity.type === 'registration' ? 'bg-primary-500/10 text-primary-400' :
                      activity.type === 'deposit' ? 'bg-emerald-500/10 text-emerald-400' :
                      activity.type === 'withdrawal' ? 'bg-red-500/10 text-red-400' :
                      'bg-accent/10 text-accent'
                    }`}>
                      {activity.type === 'registration' ? <UserPlus className="w-3.5 h-3.5" /> :
                       activity.type === 'deposit' ? <ArrowDownCircle className="w-3.5 h-3.5" /> :
                       activity.type === 'withdrawal' ? <ArrowUpCircle className="w-3.5 h-3.5" /> :
                       <TrendingUp className="w-3.5 h-3.5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-200 truncate">{activity.description}</p>
                      <p className="text-xs text-slate-500">{new Date(activity.time).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                    {activity.amount && <span className="text-sm font-medium text-white">{formatCurrency(activity.amount)}</span>}
                  </div>
                ))}
                {(!stats?.recentActivity || stats.recentActivity.length === 0) && (
                  <p className="text-sm text-slate-500 text-center py-6">No recent activity</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Pending Actions */}
          <Card>
            <CardHeader><h2 className="text-sm font-semibold text-white">Pending Actions</h2></CardHeader>
            <CardContent>
              <div className="space-y-1">
                {pendingActions.map((action, i) => {
                  const Icon = action.icon;
                  return (
                    <Link key={i} to={action.link} className="flex items-center justify-between p-3 rounded-xl hover:bg-white/[0.04] transition-colors group">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-white/[0.04] flex items-center justify-center">
                          <Icon className="w-4 h-4 text-slate-400" />
                        </div>
                        <span className="text-sm text-slate-300">{action.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-bold ${action.count > 0 ? 'text-amber-400' : 'text-slate-500'}`}>{action.count}</span>
                        <ChevronRight className="w-3 h-3 text-slate-600 group-hover:text-primary-400 transition-colors" />
                      </div>
                    </Link>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* MLM Overview */}
          <Card>
            <CardHeader><h2 className="text-sm font-semibold text-white">MLM Overview</h2></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {[
                  { label: 'Team Members', value: stats?.totalTeamMembers || 0 },
                  { label: 'Active Team', value: stats?.activeTeamMembers || 0 },
                  { label: 'Direct Referrals', value: stats?.totalDirectReferrals || 0 },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-slate-400">{item.label}</span>
                    <span className="text-sm font-semibold text-white">{formatNumber(item.value)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* System Health */}
          <Card>
            <CardHeader><h2 className="text-sm font-semibold text-white">System Health</h2></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {[
                  { label: 'API Server', status: 'Operational', ok: true },
                  { label: 'Database', status: 'Connected', ok: true },
                  { label: 'Scheduler', status: 'Running', ok: true },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-slate-400">{item.label}</span>
                    <div className="flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${item.ok ? 'bg-emerald-400' : 'bg-red-400'}`} />
                      <span className="text-xs text-slate-500">{item.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
